<template>
  <div class="warehouses-list-container warehouses-list-main warehouses-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Manage Support Lists', 'Warehouses List']" />

    <div class="glass-tab-view glass-tab-view--flush warehouses-list-glass-shell">
      <div class="glass-tab-view__content warehouses-list-tab-content warehouses-list-page-stack">
        <div class="warehouses-list-header-block">
          <div class="warehouses-list-header-row">
            <div class="warehouses-list-header-row__text">
              <h2>Warehouses</h2>
              <p class="warehouses-list-header-row__sub">Manage your company warehouses</p>
            </div>
            <div class="warehouses-list-header-actions">
              <PrimaryButton label="Add New Warehouse" icon="bi bi-plus-circle" @click="showAddWarehouseModal = true" />
            </div>
          </div>
        </div>

        <div class="warehouses-list-filters">
          <div class="warehouses-list-filter-group">
            <label for="warehouses-search">Search</label>
            <input
              id="warehouses-search"
              type="text"
              v-model="searchQuery"
              placeholder="Search warehouses..."
              class="warehouses-list-search-input"
              @input="handleSearch"
            />
          </div>
          <div class="warehouses-list-filter-group">
            <label for="warehouses-status">Status</label>
            <select
              id="warehouses-status"
              v-model="statusFilter"
              @change="handleStatusFilter"
              class="warehouses-list-filter-select"
            >
              <option value="">All Warehouses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        <div class="warehouses-list-glass-panel warehouses-list-main-card">
          <div class="consultant-glass-table-section warehouses-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed warehouses-list-content-wrapper"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table warehouses-list-table">
                  <thead>
                    <tr>
                      <th class="warehouses-list-col-name">Name</th>
                      <th class="warehouses-list-col-description">Description</th>
                      <th class="warehouses-list-col-address">Address</th>
                      <th class="warehouses-list-col-contact">Contact</th>
                      <th class="warehouses-list-col-status">Status</th>
                      <th class="warehouses-list-actions-header warehouses-list-col-actions">
                        <span class="warehouses-list-actions-header-text">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="6" class="consultant-glass-table-no-records warehouses-list-loading">
                        Loading warehouses...
                      </td>
                    </tr>
                    <tr v-else-if="filteredWarehouses.length === 0">
                      <td colspan="6" class="consultant-glass-table-no-records warehouses-list-no-data">
                        No warehouses found
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(warehouse, idx) in paginatedWarehouses"
                        :key="warehouse.id"
                        class="consultant-glass-table-row warehouses-list-row"
                        :class="{ processing: actionLoading && pendingAction?.warehouse?.id === warehouse.id }"
                        :style="{ '--wl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td class="warehouses-list-name warehouses-list-col-name">
                          <div class="warehouses-list-name-content">
                            <span class="warehouses-list-name-text">{{ warehouse.name }}</span>
                          </div>
                        </td>
                        <td class="warehouses-list-description warehouses-list-col-description">
                          <span v-if="warehouse.description && warehouse.description.trim()">{{ warehouse.description }}</span>
                          <span v-else class="warehouses-list-empty">-</span>
                        </td>
                        <td class="warehouses-list-address warehouses-list-col-address">
                          <div
                            v-if="
                              (warehouse.address && warehouse.address.trim()) ||
                              (warehouse.city && warehouse.city.trim()) ||
                              (warehouse.state && warehouse.state.trim())
                            "
                          >
                            <div v-if="warehouse.address && warehouse.address.trim()">{{ warehouse.address }}</div>
                            <div v-if="(warehouse.city && warehouse.city.trim()) || (warehouse.state && warehouse.state.trim())">
                              {{ warehouse.city || ''
                              }}{{ warehouse.city && warehouse.city.trim() && warehouse.state && warehouse.state.trim() ? ', ' : ''
                              }}{{ warehouse.state || '' }}
                            </div>
                            <div v-if="warehouse.zipCode && warehouse.zipCode.trim()">{{ warehouse.zipCode }}</div>
                          </div>
                          <span v-else class="warehouses-list-empty">-</span>
                        </td>
                        <td class="warehouses-list-contact warehouses-list-col-contact">
                          <div v-if="(warehouse.phone && warehouse.phone.trim()) || (warehouse.email && warehouse.email.trim())">
                            <div v-if="warehouse.phone && warehouse.phone.trim()">{{ warehouse.phone }}</div>
                            <div v-if="warehouse.email && warehouse.email.trim()">{{ warehouse.email }}</div>
                          </div>
                          <span v-else class="warehouses-list-empty">-</span>
                        </td>
                        <td class="warehouses-list-status warehouses-list-col-status">
                          <span :class="['warehouses-list-status-badge', warehouse.isActive ? 'active' : 'inactive']">
                            {{ warehouse.isActive ? 'Active' : 'Inactive' }}
                          </span>
                        </td>
                        <td class="warehouses-list-actions-cell warehouses-list-col-actions">
                          <div class="warehouses-list-action-buttons" role="group" aria-label="Warehouse actions">
                            <SecondaryButton
                              label=""
                              icon="bi bi-pencil"
                              custom-class="warehouses-list-action-btn warehouses-list-action-btn--edit"
                              :disabled="actionLoading"
                              aria-label="Edit"
                              title="Edit"
                              @click="editWarehouse(warehouse)"
                            />
                            <SecondaryButton
                              v-if="warehouse.isActive"
                              label=""
                              icon="bi bi-pause-fill"
                              custom-class="warehouses-list-action-btn warehouses-list-action-btn--pause"
                              :disabled="actionLoading"
                              aria-label="Deactivate"
                              title="Deactivate"
                              @click="deactivateWarehouse(warehouse)"
                            />
                            <SecondaryButton
                              v-else
                              label=""
                              icon="bi bi-play-fill"
                              custom-class="warehouses-list-action-btn warehouses-list-action-btn--play"
                              :disabled="actionLoading"
                              aria-label="Activate"
                              title="Activate"
                              @click="activateWarehouse(warehouse)"
                            />
                            <SecondaryButton
                              label=""
                              icon="bi bi-trash"
                              custom-class="consultant-secondary-btn--danger warehouses-list-action-btn warehouses-list-action-btn--delete"
                              :disabled="actionLoading"
                              aria-label="Delete"
                              title="Delete"
                              @click="deleteWarehouse(warehouse)"
                            />
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="warehouses-list-pagination-wrapper">
              <PaginationComponent
                :current-page="currentPage"
                :total-items="filteredWarehouses.length"
                :items-per-page="itemsPerPage"
                item-label="warehouses"
                :show-items-per-page="true"
                @page-change="handlePageChange"
                @items-per-page-change="handleItemsPerPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewWarehouse
      :visible="showAddWarehouseModal"
      :companyId="companyId"
      @ok="onAddWarehouseOk"
      @cancel="closeAddWarehouseModal"
    />

    <EditWarehouse
      :visible="showEditWarehouseModal"
      :warehouse="selectedWarehouse"
      :companyId="companyId"
      @ok="onEditWarehouseOk"
      @cancel="closeEditWarehouseModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      @close="closeSuccessModal"
    />

    <ErrorModal :show="showErrorModal" title="Error" :message="errorMessage" @close="closeErrorModal" />

    <WarningModal
      :show="showWarningModal"
      :title="warningTitle"
      :message="warningMessage"
      buttonText="Cancel"
      :confirmButtonText="actionLoading ? 'Processing...' : 'Confirm'"
      @close="onWarningClose"
      @confirm="confirmAction"
    />
  </div>
</template>

<script>
import '../styles/WarehousesList.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import AddNewWarehouse from './AddNewWarehouse.vue';
import EditWarehouse from './EditWarehouse.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'WarehousesList',
  components: {
    BrucdcumHeader,
    PaginationComponent,
    AddNewWarehouse,
    EditWarehouse,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      warehouses: [],
      loading: false,
      searchQuery: '',
      statusFilter: '',
      currentPage: 1,
      itemsPerPage: 10,
      showAddWarehouseModal: false,
      showEditWarehouseModal: false,
      selectedWarehouse: null,
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      showErrorModal: false,
      errorMessage: '',
      showWarningModal: false,
      warningTitle: '',
      warningMessage: '',
      pendingAction: null,
      actionLoading: false,
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id;
    },
    filteredWarehouses() {
      let filtered = [...this.warehouses];

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (warehouse) =>
            warehouse.name.toLowerCase().includes(query) ||
            (warehouse.description && warehouse.description.toLowerCase().includes(query)) ||
            (warehouse.address && warehouse.address.toLowerCase().includes(query)) ||
            (warehouse.city && warehouse.city.toLowerCase().includes(query))
        );
      }

      if (this.statusFilter !== '') {
        const isActive = this.statusFilter === 'true';
        filtered = filtered.filter((warehouse) => warehouse.isActive === isActive);
      }

      return filtered;
    },
    paginatedWarehouses() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredWarehouses.slice(start, end);
    },
  },
  mounted() {
    this.fetchWarehouses();
  },
  methods: {
    async fetchWarehouses() {
      this.loading = true;
      try {
        const response = await ApiService.get(`/warehouses/company/${this.companyId}`);

        if (response.data && response.data.success && response.data.data && response.data.data.warehouses) {
          this.warehouses = response.data.data.warehouses;
        } else {
          logger.warn('Unexpected API response structure:', response.data);
          this.warehouses = [];
        }
      } catch (error) {
        logger.error('Error fetching warehouses:', error);
        this.errorMessage = 'Failed to fetch warehouses';
        this.showErrorModal = true;
        this.warehouses = [];
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
    },
    handleStatusFilter() {
      this.currentPage = 1;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
    },
    onAddWarehouseOk(warehouse) {
      this.showAddWarehouseModal = false;
      this.warehouses.push(warehouse);
      this.successTitle = 'Success';
      this.successMessage = 'Warehouse created successfully!';
      this.showSuccessModal = true;
    },
    closeAddWarehouseModal() {
      this.showAddWarehouseModal = false;
    },
    editWarehouse(warehouse) {
      this.selectedWarehouse = { ...warehouse };
      this.showEditWarehouseModal = true;
    },
    onEditWarehouseOk(updatedWarehouse) {
      this.showEditWarehouseModal = false;
      const index = this.warehouses.findIndex((w) => w.id === updatedWarehouse.id);
      if (index !== -1) {
        this.warehouses.splice(index, 1, updatedWarehouse);
      }
      this.successTitle = 'Success';
      this.successMessage = 'Warehouse updated successfully!';
      this.showSuccessModal = true;
    },
    closeEditWarehouseModal() {
      this.showEditWarehouseModal = false;
      this.selectedWarehouse = null;
    },
    deactivateWarehouse(warehouse) {
      this.warningTitle = 'Deactivate Warehouse';
      this.warningMessage = `Are you sure you want to deactivate "${warehouse.name}"?\n\nThis warehouse will be hidden from new transactions but existing data will remain unchanged. You can reactivate it later if needed.`;
      this.pendingAction = { type: 'deactivate', warehouse };
      this.showWarningModal = true;
    },
    activateWarehouse(warehouse) {
      this.warningTitle = 'Activate Warehouse';
      this.warningMessage = `Are you sure you want to activate "${warehouse.name}"?\n\nThis will make the warehouse available for new transactions and inventory management.`;
      this.pendingAction = { type: 'activate', warehouse };
      this.showWarningModal = true;
    },
    deleteWarehouse(warehouse) {
      this.warningTitle = 'Delete Warehouse';
      this.warningMessage = `Are you sure you want to permanently delete "${warehouse.name}"?\n\nWARNING: This action cannot be undone and will permanently remove all warehouse data including:\n• Warehouse information\n• Associated inventory records\n• Historical transactions\n\nConsider deactivating instead if you want to preserve data.`;
      this.pendingAction = { type: 'delete', warehouse };
      this.showWarningModal = true;
    },
    async confirmAction() {
      if (!this.pendingAction || this.actionLoading) return;

      this.actionLoading = true;
      try {
        const { type, warehouse } = this.pendingAction;

        switch (type) {
          case 'deactivate': {
            await ApiService.delete(`/warehouses/${warehouse.id}`);
            const index = this.warehouses.findIndex((w) => w.id === warehouse.id);
            if (index !== -1) {
              this.warehouses[index].isActive = false;
            }
            this.successMessage = 'Warehouse deactivated successfully!';
            break;
          }
          case 'activate': {
            await ApiService.post(`/warehouses/${warehouse.id}/restore`);
            const index = this.warehouses.findIndex((w) => w.id === warehouse.id);
            if (index !== -1) {
              this.warehouses[index].isActive = true;
            }
            this.successMessage = 'Warehouse activated successfully!';
            break;
          }
          case 'delete': {
            await ApiService.delete(`/warehouses/${warehouse.id}?permanent=true`);
            const index = this.warehouses.findIndex((w) => w.id === warehouse.id);
            if (index !== -1) {
              this.warehouses.splice(index, 1);
            }
            this.successMessage = 'Warehouse deleted successfully!';
            break;
          }
        }

        this.successTitle = 'Success';
        this.showSuccessModal = true;
      } catch (error) {
        logger.error('Error performing warehouse action:', error);

        let errorMessage = 'Failed to perform action';

        if (error.response) {
          if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.status === 404) {
            errorMessage = 'Warehouse not found';
          } else if (error.response.status === 409) {
            errorMessage = 'Cannot perform this action due to conflicts';
          } else if (error.response.status >= 500) {
            errorMessage = 'Server error occurred. Please try again later.';
          }
        } else if (error.request) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }

        this.errorMessage = errorMessage;
        this.showErrorModal = true;
      } finally {
        this.actionLoading = false;
        this.closeWarningModal();
      }
    },
    onWarningClose() {
      if (!this.actionLoading) this.closeWarningModal();
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.pendingAction = null;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
